import { useMemo } from "react";
import { HStack } from "@design-system/HStack/HStack";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerSectionData } from "@design-system/Picker/Picker.types";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { getEntityAvatarName } from "@helpers/entityAvatarName";
import { TemplatePreviewTarget } from "./useTemplatePreviewTarget";
interface Props {
    target: TemplatePreviewTarget;
}

/**
 * Picks the entity whose data the template preview shows, grouped by client.
 * Renders nothing for a client-specific template, which previews its own client.
 */
export const TemplatePreviewSwitcher = ({ target }: Props) => {
    const { canSwitch, groups, selectedEntity, selectEntity } = target;
    const sections = useMemo<Array<PickerSectionData<number>>>(
        () =>
            groups.map(({ workspaceId, clientName, entities }) => ({
                key: workspaceId,
                title: clientName,
                items: entities.map((entity) => ({
                    value: entity.id,
                    label: getEntityAvatarName(entity),
                    leading: <EntityAvatar entity={entity} size="xs" />,
                })),
            })),
        [groups],
    );

    if (!canSwitch) {
        return null;
    }

    return (
        <Picker.Select
            sections={sections}
            value={selectedEntity?.id ?? null}
            onValueChange={(value) => value !== null && selectEntity(value)}
            searchable
            disabled={sections.length === 0}
            placeholder="Select entity"
            trigger={
                <Picker.Button variant="primary" size="md">
                    {selectedEntity && (
                        <HStack as="span" gap={6} align="center">
                            Viewing as
                            <EntityAvatar
                                entity={selectedEntity}
                                size="xs"
                                aria-hidden
                            />
                            {getEntityAvatarName(selectedEntity)}
                        </HStack>
                    )}
                </Picker.Button>
            }
        />
    );
};
